'use client'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ConnectorManager } from '../../ConnectorManager'
import { TickerItem } from '../../types'
import { useMemo } from 'react'
import { getSecureItem } from '../../utils/secureStorage'

// Query keys for consistent caching
export const BINANCE_QUERY_KEYS = {
  tickers: ['binance', 'tickers'] as const,
  userAssets: ['binance', 'userAssets'] as const,
  openOrders: ['binance', 'openOrders'] as const,
  orderHistory: (symbol: string) => ['binance', 'orderHistory', symbol] as const,
  symbols: ['binance', 'symbols'] as const,
}

const connectorManager = ConnectorManager.getInstance()

/**
 * Hook for fetching all tickers from Binance
 * Replaces useEffect pattern for ticker data
 */
export const useBinanceTickers = () => {
  return useQuery({
    queryKey: BINANCE_QUERY_KEYS.tickers,
    queryFn: async (): Promise<TickerItem[]> => {
      // Get the enabled connector (binance or ninx)
      const enabledConnector = getSecureItem('broker_enabled')
      const connector = enabledConnector === 'binance' ? 
        connectorManager.getConnector('binance') :
        enabledConnector === 'ninx' ?
          connectorManager.getConnector('ninx') : null
      if (!connector) {
        throw new Error('NINX connector not found or not connected, please sign-in or complete KYC verification to connect to NINX')
      }
      return await connector.getTickers()
    },
    refetchInterval: 30000, // Refetch every 30 seconds
    staleTime: 10000, // Consider data stale after 10 seconds
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })
}

/**
 * Hook for fetching user assets from Binance
 * Replaces useEffect pattern for holdings data
 */
export const useBinanceUserAssets = () => {
  return useQuery({
    queryKey: BINANCE_QUERY_KEYS.userAssets,
    queryFn: async () => {
      // Get the enabled connector (binance or ninx)
      // If on /x/ routes, default to ninx
      const isOnXRoute = typeof window !== 'undefined' && window.location.pathname?.startsWith('/x/')
      const enabledConnector = isOnXRoute ? 'ninx' : (getSecureItem('broker_enabled') || 'ninx')
      const connector = enabledConnector === 'binance' ?
        connectorManager.getConnector('binance') :
        enabledConnector === 'ninx' ?
          connectorManager.getConnector('ninx') : null
      if (!connector) {
        throw new Error('NINX connector not found or not connected, please sign-in or complete KYC verification to connect to NINX')
      }
      return await connector.getUserAssets()
    },
    refetchInterval: 60000, // Refetch every minute
    staleTime: 30000, // Consider data stale after 30 seconds
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })
}

/**
 * Hook for fetching open orders from Binance
 * Replaces useEffect pattern for open orders data
 */
export const useBinanceOpenOrders = () => {
  return useQuery({
    queryKey: BINANCE_QUERY_KEYS.openOrders,
    queryFn: async () => {
      // Get the enabled connector (binance or ninx)
      // If on /x/ routes, default to ninx
      const isOnXRoute = typeof window !== 'undefined' && window.location.pathname?.startsWith('/x/')
      const enabledConnector = isOnXRoute ? 'ninx' : (getSecureItem('broker_enabled') || 'ninx')
      const connector = enabledConnector === 'binance' ?
        connectorManager.getConnector('binance') :
        enabledConnector === 'ninx' ?
          connectorManager.getConnector('ninx') : null
      if (!connector) {
        throw new Error('NINX connector not found or not connected, please sign-in or complete KYC verification to connect to NINX')
      }
      return await connector.getOpenOrders()
    },
    refetchInterval: 15000, // Refetch every 15 seconds for orders
    staleTime: 5000, // Consider data stale after 5 seconds
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })
}

/**
 * Hook for fetching order history for a specific symbol
 * Replaces useEffect pattern for order history data
 */
export const useBinanceOrderHistory = (symbol: string) => {
  return useQuery({
    queryKey: BINANCE_QUERY_KEYS.orderHistory(symbol),
    queryFn: async () => {
      // Get the enabled connector (binance or ninx)
      // If on /x/ routes, default to ninx
      const isOnXRoute = typeof window !== 'undefined' && window.location.pathname?.startsWith('/x/')
      const enabledConnector = isOnXRoute ? 'ninx' : (getSecureItem('broker_enabled') || 'ninx')
      const connector = enabledConnector === 'binance' ?
        connectorManager.getConnector('binance') :
        enabledConnector === 'ninx' ?
          connectorManager.getConnector('ninx') : null
      if (!connector) {
        throw new Error('NINX connector not found or not connected, please sign-in or complete KYC verification to connect to NINX')
      }
      return await connector.getOrderHistory(symbol)
    },
    enabled: !!symbol, // Only fetch when symbol is provided
    refetchInterval: 30000, // Refetch every 30 seconds
    staleTime: 15000, // Consider data stale after 15 seconds
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })
}

/**
 * Hook for getting available symbols from tickers
 * Uses computed state instead of useEffect
 */
export const useBinanceSymbols = () => {
  const { data: tickers = [], isLoading, error } = useBinanceTickers()
  
  const symbols = useMemo(() => {
    return tickers.map(ticker => ticker.symbol)
  }, [tickers])
  
  return { symbols, isLoading, error }
}

/**
 * Hook for filtered and sorted tickers (for Watchlist)
 * Uses computed state instead of useEffect for filtering/sorting
 */
export const useFilteredTickers = (
  activeTab: string,
  favorites: string[],
  searchTerm: string,
  sortConfig: { key: keyof TickerItem | 'symbol'; direction: 'asc' | 'desc' }
) => {
  const { data: tickers = [], isLoading, error } = useBinanceTickers()
  
  const filteredAndSorted = useMemo(() => {
    let list = tickers
    
    // Filter by favorites tab
    if (activeTab === 'Favorites') {
      list = list.filter(t => favorites.includes(t.symbol))
    }
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      list = list.filter(t => t.symbol.toLowerCase().includes(term))
    }
    
    // Sort the list
    return [...list].sort((a, b) => {
      const { key, direction } = sortConfig
      if (key === 'symbol') {
        return direction === 'asc'
          ? a.symbol.localeCompare(b.symbol)
          : b.symbol.localeCompare(a.symbol)
      }
      const va = a[key] as any, vb = b[key] as any
      return direction === 'asc' ? (va < vb ? -1 : 1) : (va > vb ? -1 : 1)
    })
  }, [tickers, activeTab, favorites, searchTerm, sortConfig])
  
  return {
    filteredTickers: filteredAndSorted,
    allTickers: tickers,
    isLoading,
    error
  }
}

/**
 * Hook for invalidating queries when data needs to be refreshed
 */
export const useBinanceQueryInvalidation = () => {
  const queryClient = useQueryClient()
  
  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['binance'] })
  }
  
  const invalidateTickers = () => {
    queryClient.invalidateQueries({ queryKey: BINANCE_QUERY_KEYS.tickers })
  }
  
  const invalidateUserAssets = () => {
    queryClient.invalidateQueries({ queryKey: BINANCE_QUERY_KEYS.userAssets })
  }
  
  const invalidateOpenOrders = () => {
    queryClient.invalidateQueries({ queryKey: BINANCE_QUERY_KEYS.openOrders })
  }
  
  const invalidateOrderHistory = (symbol?: string) => {
    if (symbol) {
      queryClient.invalidateQueries({ queryKey: BINANCE_QUERY_KEYS.orderHistory(symbol) })
    } else {
      queryClient.invalidateQueries({ queryKey: ['binance', 'orderHistory'] })
    }
  }
  
  return {
    invalidateAll,
    invalidateTickers,
    invalidateUserAssets,
    invalidateOpenOrders,
    invalidateOrderHistory,
  }
} 